import { supabase } from "@/integrations/supabase/client";
import { pollColorPalette } from "./pollColorPalette";

export interface AggregateRank {
  optionId: string;
  averageRank: number;
  counts: number[];
  totalResponses: number;
  topRankResponses: number;
  topKSupport: number[];
  rankedAny: number;
  unranked: number;
}

export interface RankingResultsData {
  totals: Record<string, AggregateRank>;
  responseCount: number;
  actualResponseCount: number;
  totalsFull?: Record<string, AggregateRank>;
}

export const loadRankingResults = async (
  pollId: string,
  optionsCount: number,
  displayRankCount: number,
  { includeFullCounts = false }: { includeFullCounts?: boolean } = {}
): Promise<RankingResultsData> => {
  // First get actual response count from poll_responses table
  const { count: dbResponseCount } = await supabase
    .from("poll_responses")
    .select("*", { count: "exact", head: true })
    .eq("poll_id", pollId);

  // Note: is_ranked column exists in staging but not in production types yet
  // Select all columns to include is_ranked, then cast to proper type
  const { data, error } = await supabase
    .from("poll_allocations")
    .select("*, poll_responses!inner(poll_id)")
    .eq("poll_responses.poll_id", pollId);

  if (error) throw error;

  // Cast to include is_ranked which may exist in database but not in types
  const allocations = data as Array<{ 
    cluster_id: string; 
    points: number; 
    response_id: string; 
    is_ranked?: boolean;
  }> | null;

  const responseIds = new Set<string>();
  allocations?.forEach((alloc) => responseIds.add(alloc.response_id));

  if (!allocations || allocations.length === 0) {
    return {
      totals: {},
      responseCount: responseIds.size,
      actualResponseCount: dbResponseCount || 0,
    };
  }

  const maxRank = optionsCount;
  const overflowBuckets = optionsCount > displayRankCount ? 2 : 0;
  const countsLength = displayRankCount + overflowBuckets;
  const fullCountsLength = optionsCount + 1;
  const grouped = new Map<
    string,
    {
      total: number;
      counts: number[];
      countsFull: number[];
      responses: number;
      topRankResponses: number;
      rankedCounts: number[];
      rankedAny: number;
    }
  >();

  allocations.forEach((alloc) => {
    const current = grouped.get(alloc.cluster_id) || {
      total: 0,
      counts: Array(countsLength).fill(0),
      countsFull: Array(fullCountsLength).fill(0),
      responses: 0,
      topRankResponses: 0,
      rankedCounts: Array(displayRankCount).fill(0),
      rankedAny: 0,
    };

    const rankIndex = Math.max(0, Math.min(maxRank - 1, (alloc.points || 0) - 1));
    const isRanked = (alloc as { is_ranked?: boolean }).is_ranked !== false;
    let bucketIndex = rankIndex;

    if (overflowBuckets > 0 && rankIndex >= displayRankCount) {
      bucketIndex = isRanked ? displayRankCount : displayRankCount + 1;
    }

    if (bucketIndex >= current.counts.length) {
      bucketIndex = current.counts.length - 1;
    }

    current.counts[bucketIndex] += 1;
    if (includeFullCounts) {
      const fullBucketIndex = isRanked ? rankIndex : fullCountsLength - 1;
      current.countsFull[fullBucketIndex] += 1;
    }
    current.total += alloc.points || 0;
    current.responses += 1;
    if (isRanked) {
      current.rankedAny += 1;
      if (rankIndex < displayRankCount) {
        current.rankedCounts[rankIndex] += 1;
      }
    }
    if (bucketIndex < displayRankCount && isRanked) {
      current.topRankResponses += 1;
    }
    grouped.set(alloc.cluster_id, current);
  });

  const totals: Record<string, AggregateRank> = {};
  const totalsFull: Record<string, AggregateRank> = {};
  grouped.forEach((value, key) => {
    const avg = value.responses > 0 ? value.total / value.responses : 0;
    const topKSupport = value.rankedCounts.reduce<number[]>((acc, count, idx) => {
      const cumulative = (acc[idx - 1] || 0) + count;
      acc[idx] = cumulative;
      return acc;
    }, []);
    const unranked = Math.max(0, value.responses - value.rankedAny);
    totals[key] = {
      optionId: key,
      averageRank: avg,
      counts: value.counts,
      totalResponses: value.responses,
      topRankResponses: value.topRankResponses,
      topKSupport,
      rankedAny: value.rankedAny,
      unranked,
    };
    if (includeFullCounts) {
      totalsFull[key] = {
        optionId: key,
        averageRank: avg,
        counts: value.countsFull,
        totalResponses: value.responses,
        topRankResponses: value.topRankResponses,
        topKSupport,
        rankedAny: value.rankedAny,
        unranked,
      };
    }
  });

  return {
    totals,
    responseCount: responseIds.size,
    actualResponseCount: dbResponseCount || 0,
    totalsFull: includeFullCounts ? totalsFull : undefined,
  };
};

const generateColorScale = (
  stepsInput: number,
  scale: { hue: number; saturation: number; startLightness: number; endLightness: number }
): string[] => {
  const steps = Math.max(stepsInput, 1);
  const lightnessStep = steps > 1 ? (scale.endLightness - scale.startLightness) / (steps - 1) : 0;

  return Array.from({ length: steps }, (_, idx) => {
    const lightness = scale.endLightness - idx * lightnessStep;
    return `hsl(${scale.hue}deg ${scale.saturation}% ${lightness}%)`;
  });
};

export const generateRankColors = (
  displayRankCount: number,
  overflowMode: "none" | "single" | "dual" = "single"
): string[] => {
  const { rankingScale, lowRankOverflow, unrankedOverflow } = pollColorPalette;
  const base = generateColorScale(displayRankCount, rankingScale);

  if (overflowMode === "none") {
    return base;
  }

  if (overflowMode === "dual") {
    return [...base, lowRankOverflow, unrankedOverflow];
  }

  return [...base, lowRankOverflow]; // single overflow color (low-ranked bucket)
};

export const generateLowRankColors = (rankCount: number): string[] => {
  const { lowRankScale } = pollColorPalette;
  return generateColorScale(rankCount, lowRankScale);
};

export const formatRankLabel = (rank: number): string => {
  if (rank % 10 === 1 && rank % 100 !== 11) return `${rank}st`;
  if (rank % 10 === 2 && rank % 100 !== 12) return `${rank}nd`;
  if (rank % 10 === 3 && rank % 100 !== 13) return `${rank}rd`;
  return `${rank}th`;
};

export const getSegmentRadius = (index: number, total: number): string => {
  const radius = "0.5rem";
  if (total === 1) return radius;
  if (index === 0) return `${radius} 0 0 ${radius}`;
  if (index === total - 1) return `0 ${radius} ${radius} 0`;
  return "0";
};
